/**
 * The task drawer's edit and create fields (DD-051): title (required),
 * description, owner, the People (TD-103), and on the Tasks tab the client
 * first. The drawer footer submits it through `TASK_FORM_ID`.
 */

import React, { useMemo } from "react";
import { DropdownOption, OptionDropdown } from "@/components/ui/OptionDropdown";
import { assigneePillStyle } from "../taskAssigneeConfig";
import type { TaskAssignee, TaskAssigneeOption, TaskPerson } from "../types";
import { TaskPeopleSection } from "./TaskPeopleSection";
import {
  TEAM_VALUE,
  TaskDraft,
  assigneeDropdownOptions,
  draftClientVisible,
  draftTags,
  ownerPerson,
  tagPickerOptions,
} from "./taskDrawerForm";

export const TASK_FORM_ID = "task-drawer-form";

interface TaskFormProps {
  draft: TaskDraft;
  onChange: (patch: Partial<TaskDraft>) => void;
  onSubmit: () => void;
  people: TaskAssigneeOption[];
  peopleLoading: boolean;
  /** The task's assignee in edit mode, listed even when no longer assignable. */
  currentAssignee: TaskAssignee | null;
  /** The Tasks tab's client field. In create mode the assignee waits for
   * it; in edit mode it moves the task (DD-049). */
  clientOptions?: DropdownOption<string>[];
  /** Shown under the client field, e.g. what a move removes. */
  clientNote?: string;
  /** The brief shot list takes no client contact as assignee (DD-022). */
  excludeClientContacts?: boolean;
  /** Everyone the tag picker may offer, without the shot list's owner rule. */
  tagOptions: TaskPerson[];
  /** The viewer tags and untags people (capabilitiesFor). */
  canEditPeople: boolean;
  /** False in the portal: the picker offers only the client's people, and
   * team tags show without a remove control. */
  canTagTeam: boolean;
  /** False in the portal: the owner field offers no Team. */
  canClearOwner: boolean;
  /** Team viewers see "Visibile al cliente" as they edit. */
  showClientVisible: boolean;
}

const LABEL = "block text-sm font-medium text-gray-700 mb-1";
const FIELD =
  "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent";

export function TaskForm({
  draft,
  onChange,
  onSubmit,
  people,
  peopleLoading,
  currentAssignee,
  clientOptions,
  clientNote,
  excludeClientContacts = false,
  tagOptions,
  canEditPeople,
  canTagTeam,
  canClearOwner,
  showClientVisible,
}: TaskFormProps) {
  const assigneeOptions = useMemo(
    () => assigneeDropdownOptions(people, currentAssignee, excludeClientContacts, canClearOwner),
    [people, currentAssignee, excludeClientContacts, canClearOwner]
  );
  const owner = useMemo(
    () => ownerPerson(draft.assigneeId, [...people, ...tagOptions, currentAssignee]),
    [draft.assigneeId, people, tagOptions, currentAssignee]
  );
  const waitingForClient = clientOptions !== undefined && draft.clientId === null;

  // No `portal` on the dropdowns: a body-level menu sits outside the
  // drawer's dialog, where it can't be clicked and its click closes the drawer.
  return (
    <form
      id={TASK_FORM_ID}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
      className="space-y-4"
    >
      {clientOptions && (
        <div>
          <span id="task-drawer-client-label" className={LABEL}>
            Cliente *
          </span>
          <OptionDropdown
            value={draft.clientId}
            options={clientOptions}
            onChange={(clientId) => onChange({ clientId })}
            placeholder="Scegli un cliente"
            variant="input"
            ariaLabelledBy="task-drawer-client-label"
          />
          {clientNote && <p className="mt-1 text-xs text-gray-500">{clientNote}</p>}
        </div>
      )}

      <div>
        <label htmlFor="task-drawer-title" className={LABEL}>
          Titolo *
        </label>
        <input
          id="task-drawer-title"
          type="text"
          autoFocus
          value={draft.title}
          onChange={(event) => onChange({ title: event.target.value })}
          className={FIELD}
          placeholder="Aggiungi un task…"
        />
      </div>

      <div>
        <label htmlFor="task-drawer-description" className={LABEL}>
          Descrizione
        </label>
        <textarea
          id="task-drawer-description"
          value={draft.description}
          onChange={(event) => onChange({ description: event.target.value })}
          rows={4}
          className={`${FIELD} resize-none`}
          placeholder="Dettagli, riferimenti, indicazioni… (facoltativo)"
        />
      </div>

      <div>
        <span id="task-drawer-assignee-label" className={LABEL}>
          Assegnato a
        </span>
        <OptionDropdown
          value={draft.assigneeId ?? TEAM_VALUE}
          options={assigneeOptions}
          onChange={(value) => onChange({ assigneeId: value === TEAM_VALUE ? null : value })}
          placeholder={assigneePillStyle(null).label}
          disabled={waitingForClient}
          loading={!waitingForClient && peopleLoading}
          variant="input"
          ariaLabelledBy="task-drawer-assignee-label"
        />
      </div>

      <TaskPeopleSection
        tags={draftTags(draft)}
        clientVisible={showClientVisible && draftClientVisible(owner, draft)}
        options={canEditPeople ? tagPickerOptions(tagOptions, draft, canTagTeam) : undefined}
        onAdd={canEditPeople ? (person) => onChange({ people: [...draft.people, person] }) : undefined}
        canRemove={(person) => canEditPeople && (canTagTeam || person.kind === "client")}
        onRemove={(personId) => onChange({ people: draft.people.filter((person) => person.id !== personId) })}
      />
    </form>
  );
}
